import { AlertTriangle, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/apiClient";
import RecentPaymentsTable from "./RecentPaymentsTable";

interface Payment {
	id: string;
	name: string;
	amount: number;
	status: string;
	createdAt: string;
	failureReason: string | null;
}

export default function RecentPayments() {
	const navigate = useNavigate();
	const [recentPayments, setRecentPayments] = useState<Payment[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoading, setIsLoading] = useState(true);
	const [isPaginating, setIsPaginating] = useState(false);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [alertDismissed, setAlertDismissed] = useState(
		() => localStorage.getItem("recentPaymentsAlertDismissed") === "true",
	);

	const fetchRecentPayments = useCallback(async (page: number, isPaginationAction = false) => {
		try {
			if (isPaginationAction) {
				setIsPaginating(true);
			} else {
				setIsLoading(true);
			}

			const response = await apiClient().get(`/payments?page=${page}&limit=5`);
			setRecentPayments(response.data);

			// Check if there are more pages by trying to fetch next page
			// This is a simple approach - you might want to modify your API to return pagination metadata
			try {
				const nextPageResponse = await apiClient().get(`/payments?page=${page + 1}&limit=5`);
				setHasNextPage(nextPageResponse.data.length > 0);
			} catch {
				setHasNextPage(false);
			}
		} catch (error) {
			console.error("Failed to fetch recent payments:", error);
		} finally {
			if (isPaginationAction) {
				setIsPaginating(false);
			} else {
				setIsLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		fetchRecentPayments(1); // Only fetch initial page on mount
	}, [fetchRecentPayments]);

	const handleNextPage = useCallback(async () => {
		if (hasNextPage && !isPaginating) {
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			await fetchRecentPayments(nextPage, true);
		}
	}, [hasNextPage, isPaginating, currentPage, fetchRecentPayments]);

	const handlePreviousPage = useCallback(async () => {
		if (currentPage > 1 && !isPaginating) {
			const prevPage = currentPage - 1;
			setCurrentPage(prevPage);
			await fetchRecentPayments(prevPage, true);
		}
	}, [currentPage, isPaginating, fetchRecentPayments]);

	if (isLoading && recentPayments.length === 0) {
		return (
			<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
				<h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Payments</h2>
				<div className="text-center py-8">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4" />
					<p className="text-gray-500">Loading payments...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{recentPayments.length === 0 && !alertDismissed ? (
				<div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
					<div className="flex items-start gap-3">
						<AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
						<div className="flex-1">
							<p className="text-amber-800 text-sm sm:text-base font-medium mb-1">
								Just a heads up...
							</p>
							<div className="text-amber-700 text-sm mb-3 flex flex-col gap-1">
								<p>
									Your first payout to your bank account takes up to 7 days due to fraud checks.
								</p>
								<p>Subsequent payouts are processed the same day.</p>
							</div>
						</div>
						<button
							type="button"
							onClick={() => {
								localStorage.setItem("recentPaymentsAlertDismissed", "true");
								setAlertDismissed(true);
							}}
							className="text-amber-500 hover:text-amber-700 flex-shrink-0 transition-colors"
							aria-label="Dismiss"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				</div>
			) : null}
			<RecentPaymentsTable
				recentPayments={recentPayments}
				navigate={navigate}
				onNextPage={handleNextPage}
				onPreviousPage={handlePreviousPage}
				currentPage={currentPage}
				hasNextPage={hasNextPage}
				isPaginating={isPaginating}
			/>
		</div>
	);
}
